import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, ListGroup, Form, Button, Card } from 'react-bootstrap';
import { FaTrash, FaPlus, FaMinus } from 'react-icons/fa';
import Message from '../components/Message';
import Loader from '../components/Loader';
import { addToCart, removeFromCart, clearCartItems } from '../slices/cartSlice';
import { useCreateOpeningStoreStockMutation } from '../slices/openingStoreStocksApiSlice';
import { useGetSuppliersQuery } from '../slices/suppliersApiSlice';
import { toast } from 'react-toastify';

const OpeningStoreStocksForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const cart = useSelector((state) => state.cart);
  const { cartItems } = cart;

  const [supplierSearch, setSupplierSearch] = useState('');
  const [filteredSuppliers, setFilteredSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [note, setNote] = useState('');
  const [openingStoreStockDate, setOpeningStoreStockDate] = useState(new Date().toISOString().split('T')[0]);
  const [expiryDate, setExpiryDate] = useState(new Date().toISOString().split('T')[0]);
  const [costs, setCosts] = useState({}); // Track individual costs for each item
  const [totalCost, setTotalCost] = useState(0); // Track total cost

  const { data: suppliersData, isLoading, error } = useGetSuppliersQuery({ keyword: supplierSearch });
  const [createOpeningStoreStock, { isLoading: savingOpeningStoreStocks, error: openingStoreStocksError }] = useCreateOpeningStoreStockMutation();

 useEffect(() => {
  if (!cartItems.length) return; // Prevent unnecessary calculations if cart is empty

  const initialCosts = cartItems.reduce((acc, item) => {
    acc[item._id] = item.qty * item.price * 0.45;
    return acc;
  }, {});

  setCosts(initialCosts);

  setTotalCost(Object.values(initialCosts).reduce((acc, cost) => acc + cost, 0));
}, [cartItems]);

  
  useEffect(() => {
    if (suppliersData?.suppliers) {
      const filtered = suppliersData.suppliers.filter((supplier) =>
        supplier.name.toLowerCase().includes(supplierSearch.toLowerCase())
      );
      setFilteredSuppliers(filtered);
    }
  }, [supplierSearch, suppliersData]);

  const handleSupplierSelect = (supplier) => {
    setSelectedSupplier(supplier);
    setSupplierSearch(supplier.name);
    setFilteredSuppliers([]);
  };

  const adjustCostHandler = (id, adjustment) => {
    const updatedCosts = { ...costs };
    updatedCosts[id] = Math.max(0, updatedCosts[id] + adjustment); // Prevent cost from going below 0
    setCosts(updatedCosts);

    // Update total cost
    const newTotalCost = Object.values(updatedCosts).reduce((acc, cost) => acc + cost, 0);
    setTotalCost(newTotalCost);
  };

  const addToCartHandler = (product, qty) => {
    dispatch(addToCart({ ...product, qty }));
  };

  const removeFromCartHandler = (id) => {
    dispatch(removeFromCart(id));
    // Update costs and total cost
    const updatedCosts = { ...costs };
    delete updatedCosts[id];
    setCosts(updatedCosts);
    const newTotalCost = Object.values(updatedCosts).reduce((acc, cost) => acc + cost, 0);
    setTotalCost(newTotalCost);
  };

  const placeOpeningStoreStockHandler = async () => {
    try {
      if (!selectedSupplier) {
        toast.error('Please select a supplier');
        return;
      }

      if (cartItems.length === 0) {
        toast.error('Your cart is empty');
        return;
      }

      const openingStoreStocksData = {
        openingStoreStockItems: cartItems.map((item) => ({
          name: item.name,
          qty: item.qty,
          price: item.price,
          itemCost: costs[item._id], // Add the cost of each item
          images: item.images[0],
          product: item._id,
        })),
        supplierDetails: selectedSupplier._id,
        note,
        date: openingStoreStockDate,
        expiryDate,
        
      };

      const response = await createOpeningStoreStock(openingStoreStocksData).unwrap();
      console.log('Response:', response);
      dispatch(clearCartItems());
      toast.success('OpeningStoreStock successfully recorded');
      navigate('/house/HomeScreen');
    } catch (err) {
      console.error('Error:', err);
      toast.error(err?.data?.message || 'Failed to record openingStoreStocks');
    }
  };
  // For each individual cart item:
const calculateItemProfit = (totalPrice, totalCost) => {
  return (totalPrice - totalCost).toFixed(2); // Profit = TotalPrice - TotalCost
};

const calculateItemUnitCost = (totalCost, qty) => {
  return qty > 0 ? (totalCost / qty).toFixed(2) : 0; // UnitCost = TotalCost / Quantity
};

// For the entire cart:
const totalQuantity = cartItems.reduce((acc, item) => acc + item.qty, 0); // Total Quantity
const totalPrice = cartItems.reduce((acc, item) => acc + item.qty * item.price, 0); // Total Price
const profit = (totalPrice - totalCost).toFixed(2); // Total Profit = TotalPrice - TotalCost
const unitCost = totalQuantity > 0 ? (totalCost / totalQuantity).toFixed(2) : 0; // Total UnitCost
const cardCost = cartItems.reduce((acc, item) => acc + totalCost, 0);
return (
  <Row>
    <Col md={8}>
      <h1>OpeningMiniStocks Form</h1>
      {cartItems.length === 0 ? (
        <Message>
          Your cart is empty. <Button onClick={() => navigate('/house/HomeScreen')}>Go Back</Button>
        </Message>
      ) : (
        <ListGroup variant="flush">
          {cartItems.map((item) => (
            <ListGroup.Item key={item._id}>
              <Row>
                <Col md={2}>
                  {item.images?.length > 0 && (
                    <img
                      src={item.images[0]}
                      alt="Product Thumbnail"
                      style={{
                        width: '50px',
                        height: '50px',
                        objectFit: 'cover',
                      }}
                    />
                  )}
                </Col>
                <Col>{item.name}</Col>
                <Col md={2}>${item.price}</Col>
                <Col md={2}>
                  <Form.Control
                    as="select"
                    value={item.qty}
                    onChange={(e) => addToCartHandler(item, Number(e.target.value))}
                  >
                    {[...Array(item.countInStoreStock).keys()].map((x) => (
                      <option key={x + 1} value={x + 1}>
                        {x + 1}
                      </option>
                    ))}
                  </Form.Control>
                </Col>
                <Col md={2}>${(item.qty * item.price).toFixed(2)}</Col>
                <Col md={1}>
                  <Button variant="light" onClick={() => removeFromCartHandler(item._id)}>
                    <FaTrash />
                  </Button>
                </Col>
              </Row>
              <Row className="mt-2">
                <Col md={4}>Cost:</Col>
                <Col md={4}>${costs[item._id]}</Col>
                <Col md={4}>
                  <Button variant="light" onClick={() => adjustCostHandler(item._id, -100)}>
                    <FaMinus />
                  </Button>
                  <Button variant="light" onClick={() => adjustCostHandler(item._id, -10)}>
                    <FaMinus />
                  </Button>
                  <Button variant="light" onClick={() => adjustCostHandler(item._id, -1)}>
                    <FaMinus />
                  </Button>
                  <Button variant="light" onClick={() => adjustCostHandler(item._id, 1)}>
                    <FaPlus />
                  </Button>
                  <Button variant="light" onClick={() => adjustCostHandler(item._id, 10)}>
                    <FaPlus />
                  </Button>
                  <Button variant="light" onClick={() => adjustCostHandler(item._id, 100)}>
                    <FaPlus />
                  </Button>
                </Col>
              </Row>
              <Row className="mt-2">
                <Col md={6}>
                  <strong>Profit:</strong> $
                  {calculateItemProfit(item.qty * item.price, costs[item._id])}
                </Col>
                <Col md={6}>
                  <strong>Unit Cost:</strong> $
                  {calculateItemUnitCost(costs[item._id], item.qty)}
                </Col>
              </Row>
            </ListGroup.Item>
          ))}
        </ListGroup>
      )}
    </Col>
    <Col md={4}>
      <Card>
        <ListGroup variant="flush">
          <ListGroup.Item>
            <h2>Select Supplier</h2>
            {isLoading ? (
              <Loader />
            ) : error ? (
              <Message variant="danger">{error?.data?.message || 'Failed to load suppliers'}</Message>
            ) : (
              <>
                <Form.Group controlId="supplierSearch">
                  <Form.Label>Search Supplier</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Type supplier name"
                    value={supplierSearch}
                    onChange={(e) => setSupplierSearch(e.target.value)}
                  />
                </Form.Group>
                {filteredSuppliers.length > 0 && (
                  <ListGroup className="dropdown-menu mt-2">
                    {filteredSuppliers.map((supplier) => (
                      <ListGroup.Item
                        key={supplier._id}
                        action
                        onClick={() => handleSupplierSelect(supplier)}
                      >
                        {supplier.name} ({supplier.company})
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                )}
                {selectedSupplier && (
                  <div className="mt-3">
                    <strong>Selected Supplier:</strong>
                    <p>
                      {selectedSupplier.name} ({selectedSupplier.company})<br />
                      Phone: {selectedSupplier.telephone}<br />
                      Address: {selectedSupplier.address}<br />
                      SERIAL: {selectedSupplier.serialNumber}
                    </p>
                    <Form.Group controlId="note">
                      <Form.Label>Note</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter additional notes"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                      />
                    </Form.Group>
                    <Form.Group controlId="openingStoreStockDate" className="mt-2">
                      <Form.Label>Date</Form.Label>
                      <Form.Control
                        type="date"
                        value={openingStoreStockDate}
                        onChange={(e) => setOpeningStoreStockDate(e.target.value)}
                      />
                    </Form.Group>
                    <Form.Group controlId="expiryDate" className="mt-2">
                      <Form.Label>Expiry Date</Form.Label>
                      <Form.Control
                        type="date"
                        value={expiryDate}
                        onChange={(e) => setExpiryDate(e.target.value)}
                      />
                    </Form.Group>
                  </div>
                )}
              </>
            )}
          </ListGroup.Item>
          <ListGroup.Item>
            <h3>Total Summary:</h3>
            <p style={{ display: 'none' }}><strong>Card Cost:</strong> ${cardCost}</p>
            <p style={{ display: 'none' }}><strong>Unit Cost:</strong> ${unitCost}</p>
            <p><strong>Profit:</strong> ${profit}</p>
            <p><strong>TotalRev:</strong> ${totalPrice}</p>
          </ListGroup.Item>
          {openingStoreStocksError && (
            <ListGroup.Item>
              <Message variant="danger">{openingStoreStocksError.message}</Message>
            </ListGroup.Item>
          )}
          <ListGroup.Item>
            <Button
              type="button"
              className="btn-block"
              disabled={cartItems.length === 0 || !selectedSupplier}
              onClick={placeOpeningStoreStockHandler}
            >
              Submit OpeningStoreStocks
            </Button>
            {savingOpeningStoreStocks && <Loader />}
          </ListGroup.Item>
        </ListGroup>
      </Card>
    </Col>
  </Row>
);
};

export default OpeningStoreStocksForm;
